import React, { useState, useEffect, useContext, useMemo } from "react";
import VendorListTable from "./VendorListTable";
import VendorForm from "./VendorForm";
import "../Inventory/InventoryAccordion.css";
import MyContext from "../../../MyContext";
import { Plus, RefreshCw, Search } from "lucide-react";
import CreatePurchaseModal from "./CreatePurchaseModal";
import { useNavigate } from "react-router-dom";
import loader from "../../../images/Anlook.svg";

const Loader = () => {
  return (
    <div className="inv-loading-wrap">
      <div className="inv-loading-card">
        <div className="inv-spinner" />
        <div className="inv-loading-text">Loading vendors…</div>
        <div style={{ marginLeft: 6 }}>
          <img src={loader} alt="loading" height={26} />
        </div>
      </div>
    </div>
  );
};

const VendorManagement = ({ userID }) => {
  const [formMode, setFormMode] = useState(null); // 'add' | 'edit'
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseVendor, setPurchaseVendor] = useState(null);

  const [q, setQ] = useState("");

  const { baseUrl } = useContext(MyContext);
  const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";
  const navigate = useNavigate();

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getVendors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ userid: userID }),
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        setVendors(Array.isArray(data.response) ? data.response : []);
      } else {
        console.warn("Get vendors failed:", data);
        setVendors([]);
      }
    } catch (err) {
      console.error("API Error:", err);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const addVendor = async (vendorData) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/addVendor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ ...vendorData, userid: userID }),
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        fetchVendors();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const editVendor = async (vendorData) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/editVendor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ ...vendorData, userid: userID }),
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        fetchVendors();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/deleteVendor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ userid: userID, vendorId }),
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        fetchVendors();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePurchaseClick = (vendor) => {
    navigate("/Inventory/Vendors/CreatePurchase", {
      state: { vendor },
    });
  };

  const handleSubmitPurchase = async (data) => {
    try {
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/createPurchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ ...data, userid: userID }),
      });
      const result = await res.json();
      alert(result.responseMessage || "Purchase Created");
      setShowPurchaseModal(false);
      setPurchaseVendor(null);
    } catch (err) {
      console.error(err);
      alert("Error creating purchase");
    }
  };

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  const handleAddClick = () => {
    setFormMode("add");
    setSelectedVendor(null);
  };

  const handleEditClick = (vendor) => {
    setFormMode("edit");
    setSelectedVendor({ ...vendor, vendorId: vendor.id || vendor.vendorId });
  };

  const closeForm = () => {
    setFormMode(null);
    setSelectedVendor(null);
  };

  // Global search (same vibe as inventory)
  const filteredVendors = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return vendors;

    return vendors.filter((v) => {
      const name = String(v?.vendorName || "").toLowerCase();
      const type = String(v?.vendorType || "").toLowerCase();
      const cat = String(v?.vendorCategory || "").toLowerCase();
      const mobile = String(v?.mobile || "").toLowerCase();
      const gst = String(v?.gst || "").toLowerCase();
      const city = String(v?.city || "").toLowerCase();
      const state = String(v?.state || "").toLowerCase();
      return (
        name.includes(needle) ||
        type.includes(needle) ||
        cat.includes(needle) ||
        mobile.includes(needle) ||
        gst.includes(needle) ||
        city.includes(needle) ||
        state.includes(needle)
      );
    });
  }, [vendors, q]);

  return (
    <div className="inv-industrial-wrap">
      {/* Inventory-like Header */}
      <div className="inv-industrial-head">
        <div className="inv-industrial-title">
           <h2 className="CreateCampaign">Vendor Management</h2>
          {/* <div className="inv-industrial-sub">
            Table Source: <span className="inv-mono">/api/Inventory/v1.0/getVendors</span>
          </div> */}
        </div>

        <div className="inv-industrial-actions">
          {/* Global Search */}
          <div className="inv-industrial-search">
            <Search size={16} />
            <input
              placeholder="Search name / type / category / mobile / GST / city…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={loading}
            />
          </div>

          <button className="inv-btn inv-btn-primary" onClick={handleAddClick} disabled={loading} type="button">
            <Plus size={16} />
            Add Vendor
          </button>

          <button className="inv-btn inv-btn-ghost" onClick={fetchVendors} disabled={loading} type="button" title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <Loader />
      ) : (
        <div className="vendor-layout" style={{ display: "grid",  gap: 14 }}>
          <div className="vendor-left-panel">
            <VendorListTable
              vendors={filteredVendors}
              onEdit={handleEditClick}
              onDelete={deleteVendor}
              onCreatePurchase={handleCreatePurchaseClick}
            />
          </div>

          <div className="vendor-right-panel">
            {formMode && (
              <div className="inv-industrial-card" style={{ padding: 14 }}>
                <VendorForm
                  mode={formMode}
                  vendor={selectedVendor}
                  onClose={closeForm}
                  onSave={formMode === "add" ? addVendor : editVendor}
                />
              </div>
            )}
          </div>

          {showPurchaseModal && purchaseVendor && (
            <CreatePurchaseModal
              vendor={purchaseVendor}
              userID={userID}
              baseUrl={baseUrl}
              BASIC_AUTH={BASIC_AUTH}
              onClose={() => {
                setShowPurchaseModal(false);
                setPurchaseVendor(null);
              }}
              onSubmit={handleSubmitPurchase}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
